import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users
} from "lucide-react";
import { useDutyRosterManagement } from "@/hooks/useDutyRosterManagement";
import { DutyRosterResidencyType } from "@/types/enums";
import { DutyRosterGroup } from "@/types";
import { getResidencyOptions, getResidencyLabel, normalizeResidency, RESIDENCY_VALUES } from '@/utils/residencyUtils';
import { useAuth } from "@/contexts/auth";
import { toast } from "sonner";

interface DutyRosterGroupsProps {
  companyId: string;
}

interface GroupFormData {
  group_name: string;
  residency_type: string;
}

export function DutyRosterGroups({ companyId }: DutyRosterGroupsProps) {
  useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DutyRosterGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<DutyRosterGroup | null>(null);
  const [formData, setFormData] = useState<GroupFormData>({
    group_name: '',
    residency_type: DutyRosterResidencyType.NON_RESIDENCE
  });

  const {
    groups,
    groupsLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    isCreating,
    isUpdating,
    isDeleting
  } = useDutyRosterManagement(companyId);

  const handleCreateGroup = async () => {
    if (!formData.group_name.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      await createGroup({
        group_name: formData.group_name.trim(),
        company_id: companyId,
        residency_type: formData.residency_type
      });
      
      setIsCreateModalOpen(false);
      setFormData({
        group_name: '',
        residency_type: DutyRosterResidencyType.NON_RESIDENCE
      });
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const handleEditGroup = async () => {
    if (!editingGroup || !formData.group_name.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      await updateGroup({
        groupId: editingGroup.id,
        updates: {
          group_name: formData.group_name.trim(),
          residency_type: formData.residency_type
        }
      });
      
      setEditingGroup(null);
      setFormData({
        group_name: '',
        residency_type: DutyRosterResidencyType.NON_RESIDENCE
      });
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    
    try {
      await deleteGroup(deletingGroup.id);
      setDeletingGroup(null);
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const openDeleteDialog = (group: DutyRosterGroup) => {
    setDeletingGroup(group);
  };

  const openEditModal = (group: DutyRosterGroup) => {
    setEditingGroup(group);
    setFormData({
      group_name: group.group_name,
      residency_type: group.residency_type
    });
  };

  const closeModals = () => {
    setIsCreateModalOpen(false);
    setEditingGroup(null);
    setDeletingGroup(null);
    setFormData({
      group_name: '',
      residency_type: DutyRosterResidencyType.NON_RESIDENCE
    });
  };

  // Define columns for the DataTable
  const columns: DataTableColumn<DutyRosterGroup>[] = [
    {
      key: 'group_name',
      header: 'Group Name',
      sortable: true,
      searchable: true,
      render: (value) => <span className="font-medium">{value as string}</span>
    },
    {
      key: 'residency_type',
      header: 'Residency Type',
      sortable: true,
      searchable: true,
      render: (value, row) => (
        <Badge 
          variant={normalizeResidency(row.residency_type) === RESIDENCY_VALUES.RESIDENCE ? "default" : "secondary"}
        >
          {getResidencyLabel(row.residency_type)}
        </Badge>
      )
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (value) => new Date(value as string).toLocaleDateString()
    },
    {
      key: 'actions' as keyof DutyRosterGroup,
      header: 'Actions',
      sortable: false,
      searchable: false,
      render: (_, row) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(row)}
            disabled={isUpdating}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteDialog(row)}
            disabled={isDeleting}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  if (groupsLoading) {
    return (
      <Card className="shadow-sm border-border/50">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <span className="ml-3 text-muted-foreground">Loading groups...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-sm border-border/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Duty Roster Groups</h2>
                <p className="text-sm text-muted-foreground">
                  Manage duty roster groups for 24x7 operations
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create Group</span>
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Groups List */}
      <Card className="shadow-sm border-border/50">
        <CardContent className="p-0">
          {groups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No groups yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Create your first duty roster group to get started
              </p>
              <Button 
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-4"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Group
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border bg-card">
              <DataTable
                data={groups as unknown as Record<string, unknown>[]}
                columns={columns as unknown as DataTableColumn<Record<string, unknown>>[]}
                isLoading={groupsLoading}
                config={{
                  searchPlaceholder: "Search by group name or residency type...",
                  defaultSortField: 'group_name',
                  defaultSortDirection: 'asc',
                  rowsPerPage: 10,
                  showSearch: true,
                  showColumnVisibility: false,
                  showExport: false,
                  showPagination: true,
                  showRefresh: false,
                  emptyStateMessage: "No groups found",
                  noSearchResultsMessage: "No groups match your search criteria"
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Group Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Duty Roster Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group_name">Group Name *</Label>
              <Input
                id="group_name"
                value={formData.group_name}
                onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))}
                placeholder="Enter group name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="residency_type">Residency Type *</Label>
              <Select
                value={formData.residency_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, residency_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select residency type" />
                </SelectTrigger>
                <SelectContent>
                  {getResidencyOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={closeModals}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateGroup}
                disabled={isCreating || !formData.group_name.trim()}
              >
                {isCreating ? "Creating..." : "Create Group"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog open={!!editingGroup} onOpenChange={() => setEditingGroup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Duty Roster Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_group_name">Group Name *</Label>
              <Input
                id="edit_group_name"
                value={formData.group_name}
                onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))}
                placeholder="Enter group name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_residency_type">Residency Type *</Label>
              <Select
                value={formData.residency_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, residency_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select residency type" />
                </SelectTrigger>
                <SelectContent>
                  {getResidencyOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={closeModals}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditGroup}
                disabled={isUpdating || !formData.group_name.trim()}
              >
                {isUpdating ? "Updating..." : "Update Group"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingGroup} onOpenChange={() => setDeletingGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the group "{deletingGroup?.group_name}"? 
              This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingGroup(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteGroup}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Group"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
